import * as React from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Menu,
    MenuItem,
    Chip,
    CircularProgress,
    Alert,
    Box,
    Button,
    TablePagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import { Edit, Delete, Add, Visibility } from '@mui/icons-material';
import { Refresh, Business } from '@mui/icons-material';
import { TableColumn, SaveMerchantTransactionRequest } from '../../types';
import { useAppSelector, useAppDispatch } from '../../store';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { fetchMerchants, clearError } from '../../store/slices/merchantsSlice';
import {
    StyledContainer,
    StyledCard,
    StyledTableContainer,
} from './MerchantTable.styles';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { useToast } from '@/providers/ToastContext';

interface FormData {
    grossSales: string;
    interchange: string;
    reservefee: string;
    sstgfee: string;
    transactionCount: string;
    transactionAmount: string;
    refundCount: string;
    refundAmount: string;
    totalrefund: string;
    chargebackCount: string;
    chargebackAmount: string;
    chargebackRate: string;
    chargebackfee: string;
    wireCount: string;
    wireAmount: string;
    wireFeeAmount: string;
    achCount: string;
    achAmount: string;
    achFeeAmount: string;
    achfeeamount: string;
}

const MerchantTable: React.FC = () => {
    const dispatch = useAppDispatch();
    const { merchants, loading, error } = useAppSelector(
        state => state.merchants
    );
    const { showToast } = useToast();

    const [selectedPaymentType, setSelectedPaymentType] = useState<
        'wire' | 'ach'
    >('wire'); // Dropdown state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [charges, setCharges] = useState<any[]>([]);
    const initialFormData: FormData = {
        grossSales: '',
        interchange: '',
        reservefee: '',
        sstgfee: '',
        transactionCount: '',
        transactionAmount: '',
        refundCount: '',
        refundAmount: '',
        totalrefund: '',
        chargebackCount: '',
        chargebackAmount: '',
        chargebackRate: '',
        chargebackfee: '',
        wireCount: '',
        wireAmount: '',
        wireFeeAmount: '',
        achCount: '',
        achAmount: '',
        achFeeAmount: '',
        achfeeamount: '',
    };

    const [formData, setFormData] = useState(initialFormData);
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(
        null
    );
    const open = Boolean(anchorEl);

    const handleClick = (
        event: React.MouseEvent<HTMLElement>,
        merchantId: number
    ) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedMerchantId(merchantId);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuClick = (option: string) => {
        handleClose();
        if (option === 'charge' && selectedMerchantId) {
            navigate(`/charges/create/${selectedMerchantId}`);
        } else if (option === 'bankinfo' && selectedMerchantId) {
            navigate(`/bankinfo/${selectedMerchantId}`);
        } else if (option === 'transaction' && selectedMerchantId) {
            navigate(`/transaction/${selectedMerchantId}`);
        }
    };

    useEffect(() => {
        dispatch(
            fetchMerchants({ limit: rowsPerPage, offset: page * rowsPerPage })
        );
    }, [dispatch, page, rowsPerPage]);

    // Fetch charges only once on component mount
    useEffect(() => {
        const fetchCharges = async () => {
            try {
                const response = await apiService.getCharges();
                console.log('getCharges', response);
                if (response.success && response.data?.charges) {
                    const chargesData = response.data.charges;
                    setCharges(chargesData);

                    // Set wire amount and refund rate if found in charges
                    const wireCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'Wr Amount'
                    );
                    // Set ach amount if found in charges
                    const achCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'Ach Amount'
                    );
                    const refundCharge = chargesData.find(
                        (c: any) => c.chargeCode === 'RF RATE'
                    );
                    if (wireCharge) {
                        setFormData(prev => ({
                            ...prev,
                            ...(wireCharge && {
                                wireAmount: wireCharge.value.toString(),
                            }),
                            ...(refundCharge && {
                                refundRate: refundCharge.value.toString(),
                            }),
                            ...(achCharge && {
                                achAmount: achCharge.value.toString(),
                            }),
                        }));
                    }
                }
            } catch (error) {
                console.error('Error fetching charges:', error);
            }
        };

        fetchCharges();
    }, []);

    // Handle refresh button click
    const handleRefresh = () => {
        dispatch(clearError());
        dispatch(
            fetchMerchants({ limit: rowsPerPage, offset: page * rowsPerPage })
        );
    };

    // Handle error alert close
    const handleErrorClose = () => {
        dispatch(clearError());
    };

    // Handle page change
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formDataRef = useRef<FormData>(formData);
    useEffect(() => {
        formDataRef.current = formData;
        console.log('Form data updated:', formData);
    }, [formData]);

    // Get status color
    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active':
                return 'success';
            case 'inactive':
                return 'error';
            case 'pending':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    const columns: TableColumn[] = [
        { id: 'merchantCode', label: 'Merchant ID', minWidth: 120 },
        { id: 'name', label: 'Business Name', minWidth: 150 },
        { id: 'dba', label: 'DBA', minWidth: 120 },
        { id: 'email', label: 'Email', minWidth: 200 },
        { id: 'city', label: 'City', minWidth: 120 },
        { id: 'state', label: 'State', minWidth: 80 },
        { id: 'caprate', label: 'Cap Rate', minWidth: 100, align: 'right' },
        { id: 'capacity', label: 'Capacity', minWidth: 100, align: 'right' },
        { id: 'startDate', label: 'Start Date', minWidth: 100 },
        { id: 'status', label: 'Status', minWidth: 100 },
        { id: 'actions', label: 'Actions', minWidth: 200, align: 'center' },
    ];


    const handleDeleteTransaction = async (merchantId: number, index: number) => {
        if (!merchantId) return;
        const id = Number(merchantId);
        try {
            const response = await apiService.deleteMerchant(id);
            console.log("response2", response)
            if (response.result?.affected === 1) {
                dispatch(fetchMerchants({ limit: rowsPerPage, offset: page * rowsPerPage }));

                // setCharges(prevCharges =>
                //     prevCharges.filter((_, i) => i !== index)
                // );
                showToast('Merchant deleted sucessfully', 'success');
            } else {
                showToast('Error deleting merchant', 'error');
            }
        } catch (err) {
            showToast(
                'Unexpected error occurred while deleting merchant',
                'error'
            );
        }
    };
    return (
        <StyledContainer>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom={2}
            >
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Merchant Management
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        gutterBottom
                    >
                        Manage and view merchant information
                    </Typography>
                </Box>
            </Box>

            <StyledCard>
                <CardContent>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom={2}
                    >
                        <Typography variant="h6">
                            <Business
                                sx={{ marginRight: 1, verticalAlign: 'middle' }}
                            />
                            Merchant Data ({merchants.length} merchants)
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Box>

                    {error && (
                        <Alert
                            severity="error"
                            onClose={handleErrorClose}
                            sx={{ marginBottom: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight={200}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <StyledTableContainer component={Paper}>
                                <Table
                                    stickyHeader
                                    aria-label="merchants table"
                                >
                                    <TableHead>
                                        <TableRow>
                                            {columns.map(column => (
                                                <TableCell
                                                    key={column.id}
                                                    align={column.align}
                                                    style={{
                                                        minWidth:
                                                            column.minWidth,
                                                    }}
                                                >
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {merchants.length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={columns.length}
                                                    align="center"
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        No merchants found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            merchants.map((merchant, index) => (
                                                <TableRow
                                                    hover
                                                    key={merchant.merchantCode}
                                                >
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                        >
                                                            {merchant.merchantCode ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {merchant.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {merchant.dba ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            noWrap
                                                        >
                                                            {merchant.email ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {merchant.city ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {merchant.state ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2">
                                                            {formatCurrency(
                                                                merchant.caprate ||
                                                                0
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2">
                                                            {formatCurrency(
                                                                merchant.capacity ||
                                                                0
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {merchant.startDate ||
                                                                '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={
                                                                merchant.status
                                                                    ? merchant.status
                                                                        .charAt(
                                                                            0
                                                                        )
                                                                        .toUpperCase() +
                                                                    merchant.status
                                                                        .slice(
                                                                            1
                                                                        )
                                                                        .toLowerCase()
                                                                    : 'Unknown'
                                                            }
                                                            color={
                                                                getStatusColor(
                                                                    merchant.status
                                                                ) as any
                                                            }
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="View">
                                                            <IconButton
                                                                size="small"
                                                                color="secondary"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/merchants/${merchant.id}`
                                                                    )
                                                                }
                                                            >
                                                                <Visibility fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => navigate(
                                                                    `/merchant/create/${merchant.id}`
                                                                )}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => { }}
                                                            >
                                                                <Delete fontSize="small" onClick={() =>
                                                                    handleDeleteTransaction(
                                                                        merchant.id,
                                                                        index
                                                                    )} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        {/* + Button with Dropdown */}
                                                        <Tooltip title="More">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={e =>
                                                                    handleClick(
                                                                        e,
                                                                        merchant.id
                                                                    )
                                                                }
                                                            >
                                                                <Add fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Menu
                                                            anchorEl={anchorEl}
                                                            open={open}
                                                            onClose={
                                                                handleClose
                                                            }
                                                        >
                                                            <MenuItem
                                                                onClick={() =>
                                                                    handleMenuClick(
                                                                        'charge'
                                                                    )
                                                                }
                                                            >
                                                                Charge
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={() =>
                                                                    handleMenuClick(
                                                                        'bankinfo'
                                                                    )
                                                                }
                                                            >
                                                                Bank Info
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={() =>
                                                                    handleMenuClick(
                                                                        'transaction'
                                                                    )
                                                                }
                                                            >
                                                                Transaction
                                                            </MenuItem>
                                                        </Menu>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </StyledTableContainer>

                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={-1} // Unknown total count from backend
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Rows per page:"
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                                }
                            />
                        </>
                    )}
                </CardContent>
            </StyledCard>
        </StyledContainer>
    );
};

export default MerchantTable;
